import React from 'react';
import {
    Link,
    Navigate,
    NavLink,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom';

import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Layout from '../../../../components/Layout';
import ErrorState from '../../../../components/States/Error';
import Loading from '../../../../components/States/Loading';
import { useUserStatusContext } from '../../../../context/UserStatus';
import { showMemberActivityFeature } from '../../../../constants/featureFlags';
import { withDob } from '../../../../utils/date';
import Pill from '../../../LeadsManagement/components/Pill';
import useGetOrganizationMemberById from '../../api/useGetOrganizationMemberById';
import memberSearchProps from '../../memberSearchProps';
import { OrganizationMemberDetailsRouteParams } from '../../types';
import getMemberUrl from '../../utils/getMemberUrl';
import OrganizationMemberDetailsProvider, {
    useOrganizationMemberDetailsContext
} from '../context/OrganizationMemberDetailsContext';
import DischargeHeaderNote from '../DischargeHeaderNote';
import DischargeModal from '../DischargeModal';
import DisengageHeaderNote from '../DisengageHeaderNote';
import useShowEhrRecord from '../EhrRecord/useShowEhrRecord';
import ReadmitModal from '../ReadmitModal';
import ReEnrollModal from '../ReEnrollModal';
import VacationHeaderNote from '../VacationHeaderNote';
import DeviceStatusInfo from './DeviceStatusInfo';
import MemberDetailsNav from './MemberDetailsNav';
import MonthlyCheckIn from './MonthlyCheckIn';
import * as Style from './style.module.scss';
import UserInfo from './UserInfo';

const Header: React.FC = () => {
    const { memberDetails, memberName, currentDevice, isDeviceConfigured } =
        useOrganizationMemberDetailsContext();
    const { member_info, medication_info } = memberDetails;

    const location = useLocation();
    const { showEhrRecord, isLoading: isEhrRecordLoading } = useShowEhrRecord(
        member_info.member_id
    );

    const state = location.state;

    const tabs = React.useMemo(() => {
        return [
            {
                label: 'Overview',
                link: getMemberUrl(member_info.hero_user_id, member_info.member_id),
                hasPipe: false,
                show: true
            },
            {
                label: 'EHR Record',
                link: getMemberUrl(member_info.hero_user_id, member_info.member_id, 'ehr'),
                hasPipe: false,
                show: showEhrRecord
            },
            {
                label: 'Health Assessment',
                link: getMemberUrl(member_info.hero_user_id, member_info.member_id, 'medications'),
                hasPipe: false,
                show: !!medication_info?.length
            },
            {
                label: 'Adherence',
                link: getMemberUrl(member_info.hero_user_id, member_info.member_id, 'adherence'),
                hasPipe: false,
                show: !!medication_info?.length && isDeviceConfigured
            },
            // {
            //     label: 'Caregiving',
            //     link: getMemberUrl(member_info.hero_user_id, member_info.member_id, 'caregiving'),
            //     hasPipe: true,
            //     show: !!caregiving?.length
            // },
            {
                label: 'Activity',
                link: getMemberUrl(member_info.hero_user_id, member_info.member_id, 'activity'),
                hasPipe: false,
                show: showMemberActivityFeature
            },
            {
                label: 'Demographics',
                link: getMemberUrl(member_info.hero_user_id, member_info.member_id, 'demographics'),
                hasPipe: false,
                show: true
            },
            {
                label: 'History Log',
                link: getMemberUrl(member_info.hero_user_id, member_info.member_id, 'log'),
                hasPipe: false,
                show: true
            }
        ];
    }, [
        medication_info?.length,
        member_info?.hero_user_id,
        member_info?.member_id,
        showEhrRecord,
        isDeviceConfigured
    ]);

    // Prev/next keeps the open tab, which the neighbouring member may not have. Whether
    // the EHR tab exists is only known once the clinical record loads, so hold off till then.
    const isOnHiddenTab =
        !isEhrRecordLoading &&
        tabs.some((tab) => !tab.show && tab.link.split('?')[0] === location.pathname);
    if (isOnHiddenTab) {
        return (
            <Navigate
                to={getMemberUrl(member_info.hero_user_id, member_info.member_id, 'demographics')}
                state={state}
                replace
            />
        );
    }

    const isAdmin = currentDevice?.member_role === 'PRIMARY_USER_ADMIN';
    const isDischarged = !!member_info.status_info?.discharged_at;
    const isCancelled = member_info.status_info?.status === 'CANCELLED';

    return (
        <Section noDefaultPadding paddingHorizontal="regular" marginVertical="regular">
            <div>
                <div className={Style.name}>
                    <H role="h5" noDefaultMargin>
                        {`${withDob(memberName, member_info.contact_info.date_of_birth)}${isDischarged || isCancelled ? ' (Discharged)' : ''}`}
                    </H>

                    <Pill
                        label={isAdmin ? 'Admin' : 'Read only'}
                        styles={{
                            paddingTop: '4px',
                            paddingRight: '12px',
                            paddingBottom: '4px',
                            paddingLeft: '12px',
                            textTransform: 'none',
                            fontWeight: '700',
                            fontSize: '1.6rem',
                            height: '3.2rem',
                            borderRadius: '0.8rem',
                            background: 'rgba(244, 244, 244, 1)'
                        }}
                    />
                </div>

                <div className={Style.subhead}>
                    <div className={Style.userInfoWrapper}>
                        <UserInfo />
                        {/* HWM-23225: the device is returned once the discharge is finalized,
                            so there is no live status left to show. UserInfo keeps the Device ID. */}
                        {isCancelled ? null : <DeviceStatusInfo />}
                    </div>

                    <div className={Style.nav}>
                        {tabs
                            .filter((tab) => tab.show)
                            .map((tab) => (
                                <React.Fragment key={tab.label}>
                                    {tab.hasPipe && <div className={Style.pipe} />}
                                    <NavLink end key={tab.label} to={tab.link} state={state}>
                                        {tab.label}
                                    </NavLink>
                                </React.Fragment>
                            ))}
                    </div>
                </div>
            </div>
        </Section>
    );
};

const MemberDetailsWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { id } = useParams<Required<OrganizationMemberDetailsRouteParams>>();
    const navigate = useNavigate();

    const [showDischargeModal, setShowDischargeModal] = React.useState(false);
    const [showReadmitModal, setShowReadmitModal] = React.useState(false);
    const [showReEnrollModal, setShowReEnrollModal] = React.useState(false);

    const [searchParams] = useSearchParams();

    const query = React.useMemo(() => {
        const preparedId = id ? +id : 0;
        if (searchParams.get('role') === 'caregiver') {
            return {
                hero_user_id: preparedId
            };
        }

        return {
            member_id: preparedId
        };
    }, [searchParams, id]);

    const {
        data,
        isLoading,
        isError,
        refetch: refetchMember
    } = useGetOrganizationMemberById(query);

    const { permissions } = useUserStatusContext();
    const canEdit = permissions.fleet.fleetMemberEdit;
    const canDischarge = canEdit && data?.member_info.status_info?.can_discharge;
    const canReadmit = canEdit && data?.member_info.status_info?.can_readmit;
    const canReEnroll = data?.member_info.status_info?.can_re_enroll;
    const reEnrollBlockReason = data?.member_info.status_info?.re_enroll_block_reason;
    const previousAdminEmail = data?.member_info.status_info?.previous_admin_email;
    const isCancelled = data?.member_info.status_info?.status === 'CANCELLED';

    return (
        // `searchTo` makes the header search a jump-to-member control: selecting a
        // suggestion routes straight to that member, and submitting without a
        // highlighted suggestion hands the term to the members work list instead of
        // filtering this page.
        <Layout paddingHorizontal="none" {...memberSearchProps} searchTo="/members">
            {isLoading ? (
                <Loading />
            ) : isError ? (
                <ErrorState />
            ) : data ? (
                <OrganizationMemberDetailsProvider memberDetails={data}>
                    <Section noDefaultPadding paddingHorizontal="regular">
                        <div className={Style.headerRow}>
                            <MemberDetailsNav />

                            <div className={Style.buttonGroup}>
                                {canDischarge || canReadmit ? (
                                    <Button
                                        variant="outlineSecondary"
                                        onClick={() =>
                                            canDischarge
                                                ? setShowDischargeModal(true)
                                                : setShowReadmitModal(true)
                                        }
                                    >
                                        {canDischarge ? 'Discharge' : 'Re-admit'}
                                    </Button>
                                ) : null}

                                {canEdit &&
                                    isCancelled &&
                                    (canReEnroll ? (
                                        <Button
                                            variant="outlineSecondary"
                                            onClick={() => setShowReEnrollModal(true)}
                                        >
                                            Re-enroll
                                        </Button>
                                    ) : reEnrollBlockReason === 'device_returned' ? (
                                        <div>
                                            <Button variant="outlineSecondary" disabled>
                                                Re-enroll
                                            </Button>
                                            <P size="small">
                                                Device has been returned. To re-enroll this member,
                                                use{' '}
                                                <Link to="/members?openAddMember=1">
                                                    Add new member
                                                </Link>{' '}
                                                to order a new device.
                                            </P>
                                        </div>
                                    ) : reEnrollBlockReason === 'enrolled_elsewhere' ? (
                                        <div>
                                            <Button variant="outlineSecondary" disabled>
                                                Re-enroll
                                            </Button>
                                            <P size="small">
                                                This member is already enrolled with another
                                                provider. Re-enrollment is not available.
                                            </P>
                                        </div>
                                    ) : null)}

                                <MonthlyCheckIn data={data} />
                            </div>
                        </div>
                    </Section>
                    <Section
                        noDefaultPadding
                        paddingHorizontal="regular"
                        marginVertical="regular"
                        className="vertical-rhythm-compact"
                    >
                        <VacationHeaderNote />
                        <DischargeHeaderNote statusInfo={data?.member_info.status_info} />
                        <DisengageHeaderNote reason={data?.member_info.disengaged_reason} />
                    </Section>
                    <Header />
                    {children}
                    <DischargeModal
                        externalControls={[showDischargeModal, setShowDischargeModal]}
                        memberName={data?.member_info.first_name || ''}
                        memberId={data?.member_info.member_id || 0}
                        onSuccess={refetchMember}
                    />
                    <ReadmitModal
                        memberId={data?.member_info.member_id || 0}
                        externalControls={[showReadmitModal, setShowReadmitModal]}
                        memberName={data?.member_info.first_name || ''}
                        onSuccess={(response) => {
                            if (response.member.readmitted_to) {
                                navigate(getMemberUrl(null, response.member.readmitted_to));
                            } else {
                                // fallback to member list page
                                navigate('/members?list_version=discharged_and_cancelled');
                            }
                        }}
                    />
                    <ReEnrollModal
                        memberId={data?.member_info.member_id || 0}
                        externalControls={[showReEnrollModal, setShowReEnrollModal]}
                        memberName={data?.member_info.first_name || ''}
                        deviceSerial={data?.owned_device_info?.device_serial}
                        prefillAdminEmail={previousAdminEmail}
                        onSuccess={(response) => {
                            navigate(getMemberUrl(null, response.new_member_id));
                        }}
                        onError={refetchMember}
                    />
                </OrganizationMemberDetailsProvider>
            ) : null}
        </Layout>
    );
};
export default MemberDetailsWrapper;
